import { generatePath, Link, useParams } from "react-router-dom";

import { ROUTES } from "@/routes_new";
import AllEarsTitle from "@components/AllEarsTitle";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";

import { Separator } from "@components/ui/separator";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSearch,
  refreshSearch,
  updateFullSearch,
} from "@/api/endpoints/searches";
import { Skeleton } from "@components/ui/skeleton";
import Error from "@components/ui/error";
import { ButtonGroup, ButtonGroupSeparator } from "@components/ui/button-group";
import { Button } from "@components/ui/button";
import { ChevronDownIcon, Loader2Icon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbLink,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import { msg } from "@lingui/core/macro";
import { i18n } from "@lingui/core";
import { useState } from "react";
import useActiveLanguage from "@/hooks/useActiveLanguage";
import useActiveLocale from "@/hooks/useActiveLocale";
import QueryBuilder, {
  QueryBuilderSubmitButton,
} from "@/containers/QueryBuilderNew";
import { SearchPatchFields } from "@/types/Search";
import { formatDateForApi } from "@/utils";
import { format } from "date-fns";
import { prepareQueryForCreate } from "@/containers/QueryBuilderNew/requestUtils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogFooter,
} from "@components/ui/alert-dialog";
import QueryBuilderErrors from "@/containers/QueryBuilderNew/partials/Errors";
import { toast } from "sonner";
import { QueryBuilderForm } from "@/containers/QueryBuilderNew/types";

enum SearchUpdateOption {
  Save = "save",
  SaveAndReload = "save-and-reload",
}

const SEARCH_UPDATE_OPTION_TO_LABEL = {
  [SearchUpdateOption.Save]: msg`Spara`,
  [SearchUpdateOption.SaveAndReload]: msg`Spara & ladda om`,
};

const EditAlertView = () => {
  const { t } = useLingui();
  const language = useActiveLanguage();
  const locale = useActiveLocale(language);

  // URL state
  const { orgUuid, uuid } = useParams<{
    orgUuid: string;
    uuid: string;
  }>();

  // Local storage state
  const [searchUpdateOption, setSearchUpdateOption] = useState(
    SearchUpdateOption.Save
  );

  // Server state
  const queryClient = useQueryClient();

  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
  } = useQuery({
    queryKey: [orgUuid, "searches", uuid],
    queryFn: () => getSearch(uuid),
    enabled: !!orgUuid && !!uuid,
  });

  const search = searchData?.ok ? searchData.data : undefined;
  const searchTitle = search?.title;

  function refetchSearches() {
    queryClient.invalidateQueries({
      queryKey: [orgUuid, "searches"],
    });
  }

  const { mutate: mutateRefreshSearch, isPending: isRefreshPending } =
    useMutation({
      mutationFn: () => refreshSearch(uuid),
      onSuccess: (res) => {
        if (res.ok) {
          refetchSearches();
        } else {
          toast.error(t`Misslyckades att ladda om bevakningen`);
        }
      },
      onError: () => {
        toast.error(t`Misslyckades att ladda om bevakningen`);
      },
    });

  const {
    mutate: mutateUpdateFullSearch,
    isPending: isUpdatePending,
    data: updateData,
    error: updateError,
  } = useMutation({
    mutationFn: (body: { values: SearchPatchFields; refresh?: boolean }) =>
      updateFullSearch(uuid, body.values),
    onSuccess: (res, { refresh }) => {
      if (res.ok) {
        toast.success(t`Bevakningen uppdaterades`, {
          testId: "success-message",
        });

        if (refresh) {
          mutateRefreshSearch();
          return;
        }

        refetchSearches();
      } else {
        toast.error(t`Lyckades inte uppdatera bevakningen`);
      }
    },
    onError: () => {
      toast.error(t`Lyckades inte uppdatera bevakningen`);
    },
  });

  // Local state
  const [pendingValues, setPendingValues] = useState<QueryBuilderForm>();
  const [isConfirmRefreshOpen, setIsConfirmRefreshOpen] =
    useState<boolean>(false);

  // Handlers
  function handleUpdateSearch(values: QueryBuilderForm, refresh?: boolean) {
    mutateUpdateFullSearch({
      values: {
        end_date: values.endDate ? formatDateForApi(values.endDate) : null,
        start_date: formatDateForApi(values.startDate),
        queries: values.queries.map(prepareQueryForCreate),
        industry: values.industry,
        search_type: values.type,
        title: values.title,
        information_need: values.informationNeed ?? "",
      },
      refresh,
    });
  }

  function handleConfirmRefresh() {
    if (!pendingValues) {
      toast.error(t`Misslyckades att ladda om bevakningen`);
      return;
    }

    handleUpdateSearch(pendingValues, true);
  }

  function handleSubmit(
    values: QueryBuilderForm,
    submitterName?: QueryBuilderSubmitButton
  ) {
    if (submitterName === QueryBuilderSubmitButton.SaveAndRefresh) {
      setPendingValues(values);
      setIsConfirmRefreshOpen(true);
    } else {
      return handleUpdateSearch(values);
    }
  }

  return (
    <>
      <AllEarsTitle
        title={`${L.MENTION_SETTING_SEARCH} - ${L.MENTION_SETTING_TITLE}`}
      />

      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6 max-w-7xl">
          <SidebarTrigger className="-ml-1" />

          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />

          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <Trans>Bevakningar</Trans>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {searchTitle ? (
                  <BreadcrumbLink asChild>
                    <Link
                      to={generatePath(ROUTES.MENTION_DETAIL_ORGID, {
                        uuid,
                        orgUuid,
                      })}
                    >
                      {searchTitle}
                    </Link>
                  </BreadcrumbLink>
                ) : (
                  <Skeleton className="w-24 h-6" />
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <Trans>Redigera</Trans>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <ButtonGroup className="ml-auto">
            <Button
              form="form-qb"
              disabled={isUpdatePending || isRefreshPending}
              id={
                searchUpdateOption === SearchUpdateOption.Save
                  ? QueryBuilderSubmitButton.Save
                  : QueryBuilderSubmitButton.SaveAndRefresh
              }
              name={
                searchUpdateOption === SearchUpdateOption.Save
                  ? QueryBuilderSubmitButton.Save
                  : QueryBuilderSubmitButton.SaveAndRefresh
              }
            >
              {isUpdatePending || isRefreshPending ? (
                <Loader2Icon className="animate-spin" />
              ) : null}
              {i18n._(SEARCH_UPDATE_OPTION_TO_LABEL[searchUpdateOption])}
            </Button>
            <ButtonGroupSeparator variant="muted" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon">
                  <ChevronDownIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>
                  <Trans>Spara bevakning</Trans>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuRadioGroup
                  value={searchUpdateOption}
                  onValueChange={(newValue) => {
                    setSearchUpdateOption(newValue as SearchUpdateOption);
                  }}
                >
                  <DropdownMenuRadioItem
                    value={SearchUpdateOption.Save}
                    className="items-start [&>span]:translate-y-1"
                  >
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium">
                        <Trans>Spara bevakning (standard)</Trans>
                      </p>
                      <p className="text-muted-foreground text-xs text-balance">
                        <Trans>
                          Sparar dina ändringar och tillämpar dem på de nya
                          omnämnandena.
                        </Trans>
                      </p>
                    </div>
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem
                    value={SearchUpdateOption.SaveAndReload}
                    className="items-start [&>span]:translate-y-1"
                  >
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium">
                        <Trans>Spara & ladda om bevakning</Trans>
                      </p>
                      <p className="text-muted-foreground text-xs text-balance">
                        <Trans>
                          Tillämpar dina ändringar på redan insamlade
                          omnämnanden och tar bort taggar.
                        </Trans>
                      </p>
                    </div>
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </ButtonGroup>
        </div>
      </header>

      <div className="border-b">
        <div className="w-full p-4 lg:p-6 max-w-7xl flex justify-between items-end">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold">
              <Trans>Redigera bevakning</Trans>
            </h1>
            <AccessControl level={ACCESS_LEVELS.STAFF}>
              {search?.created_by && (
                <p className="text-sm text-muted-foreground">
                  <Trans>Skapad av</Trans>: {search.created_by}
                  {search.created &&
                    `, ${format(new Date(search.created), "PPP", { locale })}`}
                </p>
              )}
            </AccessControl>
          </div>
        </div>
      </div>

      <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-4">
        {search ? (
          <>
            <QueryBuilder
              onSubmit={handleSubmit}
              initialValues={{
                endDate: search.end_date
                  ? new Date(search.end_date)
                  : undefined,
                industry: search.industry,
                type: search.search_type,
                title: search.title,
                startDate: search.start_date
                  ? new Date(search.start_date)
                  : new Date(),
                informationNeed: search.information_need ?? "",
                queries: search.queries.map((query) => ({
                  uuid: "uuid" in query ? query.uuid : undefined,
                  excludeChannelNames: query.exclude_channel_names,
                  includeChannelNames: query.include_channel_names,
                  keywords: query.keywords,
                  languages: query.languages,
                  sources: query.sources,
                  nearKeywords: query.near_keywords,
                  nearNearKeywords: query.near_near_keywords,
                  notNearKeywords: query.not_near_keywords,
                  notNearDistance: query.not_near_distance,
                  excludeTopics: query.exclude_topics,
                  mentionTypes: query.mention_types,
                  includePaywalled: query.include_paywalled,
                  minReachByChannelType: query.min_reach_by_channel_type,
                  tags: query.tags,
                  nearDistance: query.near_distance,
                })),
              }}
            />

            {updateData ? (
              updateData.ok ? null : (
                <div>
                  <Error
                    heading={t`Åh nej!`}
                    description={t`Något gick snett med att ladda sökresultaten. Vänligen försök igen senare`}
                  />

                  {updateData.data && typeof updateData.data === "object" && (
                    <QueryBuilderErrors errors={updateData.data} />
                  )}
                </div>
              )
            ) : updateError ? (
              <Error
                heading={t`Åh nej!`}
                description={t`Något gick snett med att ladda sökresultaten. Vänligen försök igen senare`}
                error={updateError}
              />
            ) : null}

            <AlertDialog
              open={isConfirmRefreshOpen}
              onOpenChange={setIsConfirmRefreshOpen}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    <Trans>Ladda om bevakning</Trans>
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    <Trans>
                      Detta kommer att ladda om bevakningen och alla tidigare
                      resultat kommer att tas bort. Är du säker?
                    </Trans>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    <Trans>Avbryt</Trans>
                  </AlertDialogCancel>
                  <AlertDialogAction onClick={handleConfirmRefresh}>
                    <Trans>Ladda om</Trans>
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : isSearchLoading ? (
          <Skeleton className="w-full h-full" />
        ) : searchError ? (
          <Error
            heading={t`Åh nej!`}
            description={t`Något gick snett med att ladda sökresultaten. Vänligen försök igen senare`}
            error={searchError}
          />
        ) : null}
      </div>
    </>
  );
};

export default EditAlertView;
